// Unit conversion + calculator formula helpers

// ---- UNIT CONVERSIONS ----
export const kgToLbs = (kg) => kg * 2.20462;
export const lbsToKg = (lbs) => lbs / 2.20462;
export const cmToInches = (cm) => cm / 2.54;
export const inchesToCm = (inch) => inch * 2.54;
export const ftInToCm = (ft, inch = 0) => (Number(ft) * 12 + Number(inch || 0)) * 2.54;
export const cmToFtIn = (cm) => {
  const totalInches = cm / 2.54;
  const ft = Math.floor(totalInches / 12);
  const inch = Math.round(totalInches - ft * 12);
  return { ft, inch };
};

// ---- BMI ----
export const calculateBMI = (weightKg, heightCm) => {
  const heightM = heightCm / 100;
  return +(weightKg / (heightM * heightM)).toFixed(1);
};

export const getBMICategory = (bmi) => {
  if (bmi < 18.5) return { label: 'Underweight', color: 'text-blue-400', bg: 'from-blue-500/20 to-cyan-500/20', border: 'border-blue-500/30' };
  if (bmi < 25) return { label: 'Normal', color: 'text-emerald-400', bg: 'from-emerald-500/20 to-green-500/20', border: 'border-emerald-500/30' };
  if (bmi < 30) return { label: 'Overweight', color: 'text-orange-400', bg: 'from-orange-500/20 to-amber-500/20', border: 'border-orange-500/30' };
  return { label: 'Obese', color: 'text-rose-400', bg: 'from-rose-500/20 to-red-500/20', border: 'border-rose-500/30' };
};

export const getHealthyWeightRange = (heightCm) => {
  const m = heightCm / 100;
  return {
    min: +(18.5 * m * m).toFixed(1),
    max: +(24.9 * m * m).toFixed(1)
  };
};

// ---- BMR / TDEE ----
export const calculateBMR = ({ gender, weightKg, heightCm, age }) => {
  const base = 10 * weightKg + 6.25 * heightCm - 5 * age;
  return gender === 'male' ? base + 5 : base - 161;
};

export const ACTIVITY_FACTORS = {
  sedentary: { factor: 1.2, label: 'Sedentary', desc: 'Little or no exercise' },
  light: { factor: 1.375, label: 'Lightly Active', desc: 'Exercise 1–3 days/week' },
  moderate: { factor: 1.55, label: 'Moderately Active', desc: 'Exercise 3–5 days/week' },
  active: { factor: 1.725, label: 'Very Active', desc: 'Hard exercise 6–7 days/week' },
  athlete: { factor: 1.9, label: 'Athlete', desc: 'Twice daily training, physical job' }
};

export const calculateTDEE = (bmr, activityKey) => {
  const f = ACTIVITY_FACTORS[activityKey]?.factor || 1.2;
  return bmr * f;
};

// ---- WATER INTAKE ----
export const calculateBaseWaterMl = (weightKg) => weightKg * 35;
export const calculateWorkoutWaterMl = (workoutHours, activityKey) => {
  // 500–1000 ml per hour; scale by intensity
  const intensity = { sedentary: 0, light: 500, moderate: 700, active: 850, athlete: 1000 };
  return (intensity[activityKey] ?? 600) * workoutHours;
};

// ---- PROTEIN ----
export const PROTEIN_RANGES = {
  fat_loss: { min: 1.6, max: 2.0, label: 'Fat Loss' },
  muscle_gain: { min: 1.8, max: 2.2, label: 'Muscle Gain' },
  maintenance: { min: 1.2, max: 1.6, label: 'Maintenance' },
  athlete: { min: 2.0, max: 2.4, label: 'Athlete' }
};

export const calculateProteinRange = (weightKg, goalKey) => {
  const r = PROTEIN_RANGES[goalKey] || PROTEIN_RANGES.maintenance;
  return {
    min: Math.round(r.min * weightKg),
    max: Math.round(r.max * weightKg),
    recommended: Math.round(((r.min + r.max) / 2) * weightKg)
  };
};

// ---- MACROS ----
export const MACRO_SPLITS = {
  fat_loss: { protein: 0.35, carbs: 0.35, fat: 0.30, label: 'Fat Loss' },
  muscle_gain: { protein: 0.30, carbs: 0.45, fat: 0.25, label: 'Muscle Gain' },
  maintenance: { protein: 0.30, carbs: 0.40, fat: 0.30, label: 'Maintenance' }
};

export const calculateMacros = (calories, goalKey) => {
  const split = MACRO_SPLITS[goalKey] || MACRO_SPLITS.maintenance;
  const proteinG = Math.round((calories * split.protein) / 4);
  const carbsG = Math.round((calories * split.carbs) / 4);
  const fatG = Math.round((calories * split.fat) / 9);
  return {
    protein: { grams: proteinG, calories: proteinG * 4, percent: Math.round(split.protein * 100) },
    carbs: { grams: carbsG, calories: carbsG * 4, percent: Math.round(split.carbs * 100) },
    fat: { grams: fatG, calories: fatG * 9, percent: Math.round(split.fat * 100) }
  };
};

// ---- CALORIE TARGETS ----
export const getCalorieTargets = (tdee) => ({
  mild: Math.round(tdee - 300),
  moderate: Math.round(tdee - 500),
  aggressive: Math.round(tdee - 700),
  muscleGain: Math.round(tdee + 300),
  maintenance: Math.round(tdee)
});

// ---- BODY FAT (US Navy) ----
const log10 = (x) => Math.log10(x);

export const calculateBodyFat = ({ gender, heightCm, waistCm, neckCm, hipCm }) => {
  if (gender === 'male') {
    if (waistCm <= neckCm) return null;
    return +(86.010 * log10(waistCm - neckCm) - 70.041 * log10(heightCm) + 36.76).toFixed(1);
  }
  if (!hipCm || waistCm + hipCm <= neckCm) return null;
  return +(163.205 * log10(waistCm + hipCm - neckCm) - 97.684 * log10(heightCm) - 78.387).toFixed(1);
};

export const getBodyFatCategory = (bf, gender) => {
  // Source: ACE common reference ranges
  if (gender === 'male') {
    if (bf < 6) return { label: 'Essential Fat', color: 'text-blue-400', border: 'border-blue-500/30' };
    if (bf < 14) return { label: 'Athletes', color: 'text-cyan-400', border: 'border-cyan-500/30' };
    if (bf < 18) return { label: 'Fitness', color: 'text-emerald-400', border: 'border-emerald-500/30' };
    if (bf < 25) return { label: 'Average', color: 'text-amber-400', border: 'border-amber-500/30' };
    return { label: 'Obese', color: 'text-rose-400', border: 'border-rose-500/30' };
  }
  if (bf < 14) return { label: 'Essential Fat', color: 'text-blue-400', border: 'border-blue-500/30' };
  if (bf < 21) return { label: 'Athletes', color: 'text-cyan-400', border: 'border-cyan-500/30' };
  if (bf < 25) return { label: 'Fitness', color: 'text-emerald-400', border: 'border-emerald-500/30' };
  if (bf < 32) return { label: 'Average', color: 'text-amber-400', border: 'border-amber-500/30' };
  return { label: 'Obese', color: 'text-rose-400', border: 'border-rose-500/30' };
};

export const calculateLBM = (weightKg, bodyFatPercent) =>
  +(weightKg * (1 - bodyFatPercent / 100)).toFixed(1);

// Estimated weekly fat loss (kg) from daily deficit
// 7700 kcal ≈ 1 kg body fat
export const weeklyFatLossKg = (dailyDeficit) =>
  +((dailyDeficit * 7) / 7700).toFixed(2);

// ---- IDEAL WEIGHT ----
// Devine formula (kg). Reference base height: 5ft (60 in = 152.4 cm).
export const idealWeightDevine = (gender, heightCm) => {
  const inchesOver5ft = Math.max(0, cmToInches(heightCm) - 60);
  const base = gender === 'male' ? 50 : 45.5;
  return +(base + 2.3 * inchesOver5ft).toFixed(1);
};

// Hamwi formula (kg). Same base 5ft.
export const idealWeightHamwi = (gender, heightCm) => {
  const inchesOver5ft = Math.max(0, cmToInches(heightCm) - 60);
  const base = gender === 'male' ? 48 : 45.5;
  const perInch = gender === 'male' ? 2.7 : 2.2;
  return +(base + perInch * inchesOver5ft).toFixed(1);
};

// Robinson formula (kg).
export const idealWeightRobinson = (gender, heightCm) => {
  const inchesOver5ft = Math.max(0, cmToInches(heightCm) - 60);
  const base = gender === 'male' ? 52 : 49;
  const perInch = gender === 'male' ? 1.9 : 1.7;
  return +(base + perInch * inchesOver5ft).toFixed(1);
};

// Miller formula (kg).
export const idealWeightMiller = (gender, heightCm) => {
  const inchesOver5ft = Math.max(0, cmToInches(heightCm) - 60);
  const base = gender === 'male' ? 56.2 : 53.1;
  const perInch = gender === 'male' ? 1.41 : 1.36;
  return +(base + perInch * inchesOver5ft).toFixed(1);
};

// ---- FFMI ----
export const calculateFFMI = (weightKg, heightCm, bodyFatPercent) => {
  const lbm = weightKg * (1 - bodyFatPercent / 100);
  const m = heightCm / 100;
  const ffmi = lbm / (m * m);
  // Adjusted FFMI normalizes to a standard 1.8m height
  const adjusted = ffmi + 6.1 * (1.8 - m);
  return { ffmi: +ffmi.toFixed(1), adjusted: +adjusted.toFixed(1), lbm: +lbm.toFixed(1) };
};

export const getFFMICategory = (ffmi, gender) => {
  // Source: Kouri et al. reference values
  if (gender === 'male') {
    if (ffmi < 18) return { label: 'Below average', color: 'text-blue-400', border: 'border-blue-500/30' };
    if (ffmi < 20) return { label: 'Average', color: 'text-cyan-400', border: 'border-cyan-500/30' };
    if (ffmi < 22) return { label: 'Above average', color: 'text-emerald-400', border: 'border-emerald-500/30' };
    if (ffmi < 25) return { label: 'Excellent', color: 'text-amber-400', border: 'border-amber-500/30' };
    return { label: 'Exceptional', color: 'text-rose-400', border: 'border-rose-500/30' };
  }
  if (ffmi < 14) return { label: 'Below average', color: 'text-blue-400', border: 'border-blue-500/30' };
  if (ffmi < 16) return { label: 'Average', color: 'text-cyan-400', border: 'border-cyan-500/30' };
  if (ffmi < 18) return { label: 'Above average', color: 'text-emerald-400', border: 'border-emerald-500/30' };
  if (ffmi < 20) return { label: 'Excellent', color: 'text-amber-400', border: 'border-amber-500/30' };
  return { label: 'Exceptional', color: 'text-rose-400', border: 'border-rose-500/30' };
};

// ---- WAIST-TO-HIP RATIO ----
export const calculateWHR = (waistCm, hipCm) => +(waistCm / hipCm).toFixed(2);

export const getWHRRisk = (whr, gender) => {
  if (gender === 'male') {
    if (whr < 0.9) return { label: 'Low risk', color: 'text-emerald-400', border: 'border-emerald-500/30' };
    if (whr < 1.0) return { label: 'Moderate risk', color: 'text-amber-400', border: 'border-amber-500/30' };
    return { label: 'High risk', color: 'text-rose-400', border: 'border-rose-500/30' };
  }
  if (whr < 0.8) return { label: 'Low risk', color: 'text-emerald-400', border: 'border-emerald-500/30' };
  if (whr < 0.85) return { label: 'Moderate risk', color: 'text-amber-400', border: 'border-amber-500/30' };
  return { label: 'High risk', color: 'text-rose-400', border: 'border-rose-500/30' };
};

// ---- HEART RATE ZONES ----
// Tanaka formula is more accurate for adults: 208 - 0.7 × age. We expose both.
export const maxHRTraditional = (age) => 220 - age;
export const maxHRTanaka = (age) => Math.round(208 - 0.7 * age);

export const heartRateZones = (age, restingHR = null) => {
  const max = maxHRTanaka(age);
  // Karvonen if restingHR provided
  const zone = (lo, hi) => {
    if (restingHR && restingHR > 30 && restingHR < max) {
      const hrr = max - restingHR;
      return { min: Math.round(restingHR + hrr * lo), max: Math.round(restingHR + hrr * hi) };
    }
    return { min: Math.round(max * lo), max: Math.round(max * hi) };
  };
  return {
    maxHR: max,
    zones: [
      { name: 'Zone 1: Recovery', desc: 'Very light, warm-up', pct: '50–60%', bpm: zone(0.5, 0.6), color: 'from-blue-500/30 to-cyan-500/30' },
      { name: 'Zone 2: Fat Burn', desc: 'Light, fat-burning', pct: '60–70%', bpm: zone(0.6, 0.7), color: 'from-cyan-500/30 to-emerald-500/30' },
      { name: 'Zone 3: Aerobic', desc: 'Moderate, endurance', pct: '70–80%', bpm: zone(0.7, 0.8), color: 'from-emerald-500/30 to-amber-500/30' },
      { name: 'Zone 4: Threshold', desc: 'Hard, lactate threshold', pct: '80–90%', bpm: zone(0.8, 0.9), color: 'from-amber-500/30 to-orange-500/30' },
      { name: 'Zone 5: Peak', desc: 'Maximum, HIIT', pct: '90–100%', bpm: zone(0.9, 1.0), color: 'from-rose-500/30 to-red-500/30' }
    ]
  };
};

// ---- VO2 MAX ----
// Cooper 12-min run test: VO2max = (distance_m - 504.9) / 44.73
export const vo2maxCooper = (distanceM) => {
  if (!distanceM || distanceM < 200) return null;
  return +((distanceM - 504.9) / 44.73).toFixed(1);
};

// Resting HR based estimate (Uth-Sorensen-Overgaard-Pedersen): VO2max = 15.3 × (HRmax / HRrest)
export const vo2maxFromHR = (age, restingHR) => {
  if (!age || !restingHR || restingHR <= 0) return null;
  return +(15.3 * (maxHRTraditional(age) / restingHR)).toFixed(1);
};

export const getVO2MaxCategory = (vo2, age, gender) => {
  // Simplified bands (ml/kg/min), Cooper Institute reference
  const bands = gender === 'male'
    ? [{ min: 0, label: 'Poor', color: 'text-rose-400' }, { min: 30, label: 'Below average', color: 'text-amber-400' }, { min: 40, label: 'Average', color: 'text-cyan-400' }, { min: 50, label: 'Good', color: 'text-emerald-400' }, { min: 60, label: 'Excellent', color: 'text-blue-400' }]
    : [{ min: 0, label: 'Poor', color: 'text-rose-400' }, { min: 24, label: 'Below average', color: 'text-amber-400' }, { min: 32, label: 'Average', color: 'text-cyan-400' }, { min: 40, label: 'Good', color: 'text-emerald-400' }, { min: 50, label: 'Excellent', color: 'text-blue-400' }];
  let band = bands[0];
  for (const b of bands) if (vo2 >= b.min) band = b;
  return { label: band.label, color: band.color, border: `border-${band.color.replace('text-', '').replace('-400', '')}-500/30` };
};

// ---- ONE REP MAX ----
// Epley: 1RM = w × (1 + reps/30). Brzycki: 1RM = w × 36/(37 - reps).
export const oneRepMaxEpley = (weight, reps) => +(weight * (1 + reps / 30)).toFixed(1);
export const oneRepMaxBrzycki = (weight, reps) => reps < 37 ? +(weight * 36 / (37 - reps)).toFixed(1) : null;

export const oneRepMaxPercentages = (oneRM) => [
  { pct: 100, reps: '1', weight: +(oneRM * 1.00).toFixed(1) },
  { pct: 95, reps: '2', weight: +(oneRM * 0.95).toFixed(1) },
  { pct: 90, reps: '3–4', weight: +(oneRM * 0.90).toFixed(1) },
  { pct: 85, reps: '5–6', weight: +(oneRM * 0.85).toFixed(1) },
  { pct: 80, reps: '6–8', weight: +(oneRM * 0.80).toFixed(1) },
  { pct: 75, reps: '8–10', weight: +(oneRM * 0.75).toFixed(1) },
  { pct: 70, reps: '10–12', weight: +(oneRM * 0.70).toFixed(1) },
  { pct: 65, reps: '12–15', weight: +(oneRM * 0.65).toFixed(1) },
  { pct: 60, reps: '15–20', weight: +(oneRM * 0.60).toFixed(1) }
];

// ---- PACE ----
// All inputs in metric (km, minutes). Convert as needed at the UI layer.
export const calculatePace = (distanceKm, totalMinutes) => {
  if (!distanceKm || !totalMinutes) return null;
  const perKmMin = totalMinutes / distanceKm;
  const perMileMin = totalMinutes / (distanceKm * 0.621371);
  const formatMinSec = (m) => {
    const mins = Math.floor(m);
    const secs = Math.round((m - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const speedKph = distanceKm / (totalMinutes / 60);
  return {
    perKm: formatMinSec(perKmMin),
    perMile: formatMinSec(perMileMin),
    perKmMin: +perKmMin.toFixed(2),
    perMileMin: +perMileMin.toFixed(2),
    speedKph: +speedKph.toFixed(2),
    speedMph: +(speedKph * 0.621371).toFixed(2)
  };
};

// Predict finish times for common race distances given current pace per km
export const racePredictions = (perKmMin) => {
  if (!perKmMin) return [];
  const races = [
    { name: '5K', km: 5 },
    { name: '10K', km: 10 },
    { name: 'Half Marathon', km: 21.0975 },
    { name: 'Marathon', km: 42.195 }
  ];
  return races.map((r) => {
    const total = perKmMin * r.km;
    const h = Math.floor(total / 60);
    const m = Math.floor(total % 60);
    const s = Math.round((total - Math.floor(total)) * 60);
    return { name: r.name, time: h > 0 ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}` : `${m}:${s.toString().padStart(2, '0')}` };
  });
};

// ---- PREGNANCY WEIGHT GAIN ----
// IOM 2009 recommendations (kg). Based on pre-pregnancy BMI category.
export const pregnancyWeightGainRanges = (prePregBMI) => {
  if (prePregBMI < 18.5) return { category: 'Underweight', total: { min: 12.5, max: 18 }, weekly2nd3rd: { min: 0.51, max: 0.58 } };
  if (prePregBMI < 25) return { category: 'Normal weight', total: { min: 11.5, max: 16 }, weekly2nd3rd: { min: 0.42, max: 0.50 } };
  if (prePregBMI < 30) return { category: 'Overweight', total: { min: 7, max: 11.5 }, weekly2nd3rd: { min: 0.28, max: 0.33 } };
  return { category: 'Obese', total: { min: 5, max: 9 }, weekly2nd3rd: { min: 0.22, max: 0.27 } };
};

// Recommended cumulative gain at a given gestational week.
// First-trimester gain ≈ 1.5–2 kg (use 1.5 as base for under, 2 for others). Then linear weekly through 2nd & 3rd tri.
export const recommendedGainAtWeek = (week, ranges) => {
  if (!week || week < 1) return { min: 0, max: 0 };
  if (week <= 13) {
    // Linear ramp to ~2 kg by week 13
    const base = ranges.category === 'Underweight' ? 2 : 2;
    return { min: +(base * (week / 13) * 0.7).toFixed(1), max: +(base * (week / 13) * 1.2).toFixed(1) };
  }
  const extraWeeks = Math.min(40, week) - 13;
  const firstTri = 2;
  return {
    min: +(firstTri + ranges.weekly2nd3rd.min * extraWeeks).toFixed(1),
    max: +(firstTri + ranges.weekly2nd3rd.max * extraWeeks).toFixed(1)
  };
};

// ---- SLEEP ----
// Sleep cycles ≈ 90 min. Add 14-min fall-asleep buffer.
export const sleepCyclesFromWake = (wakeTime, cycles = [4, 5, 6]) => {
  // wakeTime: 'HH:MM' string
  const [hh, mm] = wakeTime.split(':').map(Number);
  if (isNaN(hh) || isNaN(mm)) return null;
  const wakeMins = hh * 60 + mm;
  return cycles.map((c) => {
    const sleepMins = c * 90 + 14;
    let bedMins = wakeMins - sleepMins;
    while (bedMins < 0) bedMins += 24 * 60;
    const h = Math.floor(bedMins / 60) % 24;
    const m = bedMins % 60;
    return {
      cycles: c,
      hours: +(c * 1.5).toFixed(1),
      bedTime: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
    };
  });
};

export const sleepCyclesFromBed = (bedTime, cycles = [4, 5, 6]) => {
  const [hh, mm] = bedTime.split(':').map(Number);
  if (isNaN(hh) || isNaN(mm)) return null;
  const bedMins = hh * 60 + mm + 14; // 14-min fall-asleep buffer
  return cycles.map((c) => {
    const wakeMins = (bedMins + c * 90) % (24 * 60);
    const h = Math.floor(wakeMins / 60);
    const m = wakeMins % 60;
    return {
      cycles: c,
      hours: +(c * 1.5).toFixed(1),
      wakeTime: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
    };
  });
};

// ---- INTERMITTENT FASTING ----
export const IF_PROTOCOLS = [
  { value: '16:8', fast: 16, eat: 8, label: '16:8 (Leangains)' },
  { value: '18:6', fast: 18, eat: 6, label: '18:6' },
  { value: '20:4', fast: 20, eat: 4, label: '20:4 (Warrior)' },
  { value: 'omad', fast: 23, eat: 1, label: 'OMAD (One Meal A Day)' },
  { value: '14:10', fast: 14, eat: 10, label: '14:10 (Beginner)' }
];

export const calcFastingWindows = (startTime, protocol) => {
  const [hh, mm] = startTime.split(':').map(Number);
  if (isNaN(hh) || isNaN(mm)) return null;
  const start = hh * 60 + mm; // minutes
  const eatEnd = (start + protocol.eat * 60) % (24 * 60);
  const fastEnd = (eatEnd + protocol.fast * 60) % (24 * 60);
  const fmt = (m) => `${Math.floor(m / 60).toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}`;
  return {
    eatingStart: fmt(start),
    eatingEnd: fmt(eatEnd),
    fastingStart: fmt(eatEnd),
    fastingEnd: fmt(fastEnd)
  };
};

// ---- CALORIE BURN ----
// kcal/min = MET × 3.5 × weightKg / 200
export const ACTIVITIES_MET = [
  { value: 'walking_slow', label: 'Walking, slow (3 km/h)', met: 2.5 },
  { value: 'walking_brisk', label: 'Walking, brisk (5–6 km/h)', met: 4.3 },
  { value: 'jogging', label: 'Jogging (8 km/h)', met: 8.0 },
  { value: 'running', label: 'Running (10 km/h)', met: 10.0 },
  { value: 'cycling_light', label: 'Cycling, light', met: 5.5 },
  { value: 'cycling_mod', label: 'Cycling, moderate', met: 8.0 },
  { value: 'swimming', label: 'Swimming, moderate', met: 7.0 },
  { value: 'strength', label: 'Strength training', met: 5.0 },
  { value: 'hiit', label: 'HIIT', met: 8.5 },
  { value: 'yoga', label: 'Yoga, moderate', met: 3.0 },
  { value: 'pilates', label: 'Pilates', met: 3.5 },
  { value: 'dance', label: 'Dance, moderate', met: 5.0 },
  { value: 'rope', label: 'Skipping rope', met: 11.0 },
  { value: 'housework', label: 'Housework', met: 3.0 },
  { value: 'gardening', label: 'Gardening', met: 4.0 }
];

export const calorieBurn = (met, weightKg, minutes) =>
  Math.round((met * 3.5 * weightKg / 200) * minutes);

// ---- STEPS TO CALORIES ----
// Approx: stride length ≈ height × 0.415 (cm). 0.04 kcal per step per kg-of-body-weight is a rough lookup; here we use METs ≈ 2.5 for slow walking.
export const stepsToCalories = (steps, weightKg, heightCm) => {
  if (!steps || !weightKg) return null;
  const stride = (heightCm || 165) * 0.415; // cm
  const distanceKm = (steps * stride) / 100000;
  // ~ 0.04 kcal/step/kg (a common quick estimate scaled per kg)
  const kcal = Math.round(steps * 0.04 * (weightKg / 70));
  return { distanceKm: +distanceKm.toFixed(2), distanceMi: +(distanceKm * 0.621371).toFixed(2), kcal };
};

// ---- BMI PRIME ----
export const bmiPrime = (bmi) => +(bmi / 25).toFixed(2);
export const getBMIPrimeCategory = (bp) => {
  if (bp < 0.74) return { label: 'Underweight', color: 'text-blue-400', border: 'border-blue-500/30' };
  if (bp < 1.0) return { label: 'Normal', color: 'text-emerald-400', border: 'border-emerald-500/30' };
  if (bp < 1.2) return { label: 'Overweight', color: 'text-orange-400', border: 'border-orange-500/30' };
  return { label: 'Obese', color: 'text-rose-400', border: 'border-rose-500/30' };
};

// ---- BSA (Mosteller) ----
// BSA (m²) = sqrt((H_cm × W_kg) / 3600)
export const calculateBSA = (heightCm, weightKg) => {
  if (!heightCm || !weightKg) return null;
  return +Math.sqrt((heightCm * weightKg) / 3600).toFixed(2);
};
